import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    HeartHandshake, Users, UserSearch, Shield, Sparkles, ArrowRight,
    Home, Hospital, MapPin, CheckCircle2, Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import TopNav from "@/components/TopNav";
import { PILLARS } from "@/data/pillars";
import { LANDING } from "@/constants/testIds";
import { api } from "@/lib/api";

export const PILLAR_TESTID = "landing-pillar-card";

const PILLAR_ICONS = {
    "home-health": Home,
    "b2b-staffing": Hospital,
    "recruitment": UserSearch,
};

const PILLAR_STYLES = {
    emerald: {
        eyebrow: "text-emerald-700",
        dot: "bg-emerald-500",
        iconBg: "bg-emerald-100 group-hover:bg-emerald-700 group-hover:text-white",
        iconColor: "text-emerald-700",
        cta: "bg-emerald-700 group-hover:bg-emerald-800 text-white",
        cardBorder: "border-emerald-100 hover:border-emerald-500 hover:shadow-emerald-100",
    },
    sky: {
        eyebrow: "text-sky-700",
        dot: "bg-sky-500",
        iconBg: "bg-sky-100 group-hover:bg-sky-700 group-hover:text-white",
        iconColor: "text-sky-700",
        cta: "bg-sky-700 group-hover:bg-sky-800 text-white",
        cardBorder: "border-sky-100 hover:border-sky-500 hover:shadow-sky-100",
    },
    orange: {
        eyebrow: "text-orange-700",
        dot: "bg-orange-500",
        iconBg: "bg-orange-100 group-hover:bg-orange-600 group-hover:text-white",
        iconColor: "text-orange-700",
        cta: "bg-orange-600 group-hover:bg-orange-700 text-white",
        cardBorder: "border-orange-100 hover:border-orange-500 hover:shadow-orange-100",
    },
};

const HERO_IMG =
    "https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=1200&q=80";

export default function Landing() {
    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />

            {/* ---------- Compact intro ---------- */}
            <section data-testid={LANDING.hero} className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-10 text-center animate-fade-in">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-stone-700">Infini Health<span className="text-emerald-700">Care</span> · CareCo 24</span>
                    </div>
                    <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-[1.05] max-w-4xl mx-auto">
                        Compassionate care.<br />
                        <span className="brand-gradient-text">Delivered where it&rsquo;s needed most.</span>
                    </h1>
                    <p className="mt-5 text-lg text-stone-600 font-dmsans max-w-2xl mx-auto">
                        Home health for families, nurse staffing for hospitals and facilities,
                        and nursing recruitment for businesses. <strong className="text-stone-900">Pick a pillar to get started.</strong>
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3 justify-center" data-testid="landing-primary-ctas">
                        <Link
                            to="/auth?mode=register&role=agency"
                            data-testid="landing-post-shift-cta"
                            className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-outfit font-semibold shadow-sm transition-all"
                        >
                            Post a shift → agency sign-up
                        </Link>
                        <Link
                            to="/auth?mode=register&role=family"
                            data-testid="landing-find-care-cta"
                            className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white border-2 border-stone-200 hover:border-emerald-600 text-stone-800 hover:text-emerald-700 text-sm font-outfit font-semibold transition-all"
                        >
                            Find care for my family
                        </Link>
                    </div>
                    <p className="mt-3 text-xs text-stone-500 font-dmsans" data-testid="landing-b2b-note">
                        <strong>Agencies &amp; facilities:</strong> after sign-up, add your payment details on the Payment Settings screen — then you can post open shifts to our caregiver pool.
                    </p>
                </div>

                {/* ---------- THREE BIG PILLAR BUTTONS - immediately below intro ---------- */}
                <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {PILLARS.map((p) => {
                            const Icon = PILLAR_ICONS[p.key] || Briefcase;
                            const s = PILLAR_STYLES[p.color];
                            return (
                                <Link
                                    key={p.key}
                                    to={`/services/${p.key}`}
                                    data-testid={PILLAR_TESTID}
                                    data-pillar={p.key}
                                    className={`group relative bg-white rounded-3xl p-8 border-2 ${s.cardBorder} hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col overflow-hidden`}
                                >
                                    <div className="absolute top-4 right-4">
                                        <div className={`text-xs font-bold uppercase tracking-[0.2em] ${s.eyebrow}`}>
                                            {p.eyebrow}
                                        </div>
                                    </div>

                                    <div className={`w-16 h-16 rounded-2xl ${s.iconBg} flex items-center justify-center mb-6 transition-all duration-300`}>
                                        <Icon className={`w-8 h-8 ${s.iconColor} group-hover:text-white transition-colors`} strokeWidth={1.5} />
                                    </div>

                                    <h3 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight leading-tight">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-stone-600 font-dmsans leading-relaxed">
                                        {p.tagline}
                                    </p>

                                    <ul className="mt-5 space-y-1.5">
                                        {p.services.slice(0, 3).map((sv) => (
                                            <li key={sv.id} className="flex items-center gap-2 text-sm text-stone-700 font-dmsans">
                                                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                                {sv.label}
                                            </li>
                                        ))}
                                        <li className="text-xs text-stone-400 font-dmsans pl-3.5">
                                            +{p.services.length - 3} more services
                                        </li>
                                    </ul>

                                    {p.paymentNote && (
                                        <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-[11px] text-amber-800 font-dmsans">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                            Private pay only — no insurance billing
                                        </div>
                                    )}

                                    {/* Big CTA button inside card */}
                                    <div className={`mt-8 rounded-full ${s.cta} h-14 flex items-center justify-center gap-2 font-outfit font-semibold text-base transition-all`}>
                                        {p.primaryCta}
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Trust bar */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-stone-500 font-dmsans">
                        <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Vetted, credentialed professionals</span>
                        <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> RN · LPN · CNA · HHA</span>
                        <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Nationwide · All 50 states</span>
                    </div>
                </div>
            </section>

            {/* ---------- Care-focused imagery + promise ---------- */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">Our promise</div>
                        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900 leading-tight">
                            Care that treats <span className="brand-gradient-text">people like family</span>.
                        </h2>
                        <p className="mt-5 text-lg text-stone-600 font-dmsans">
                            Every caregiver is licensed, background-checked and matched to your needs. Every shift is documented. Every family gets a real human, not a call center.
                        </p>
                        <div className="mt-8 space-y-4">
                            <ServeItem
                                icon={Home}
                                title="Families"
                                desc="Book vetted caregivers for aging parents, post-op recovery, or ongoing companion care."
                            />
                            <ServeItem
                                icon={Hospital}
                                title="Hospitals & Nursing Homes"
                                desc="Fill open RN, LPN, CNA shifts fast — per diem, block, or travel contracts."
                            />
                            <ServeItem
                                icon={Users}
                                title="Businesses Hiring Nurses"
                                desc="Have us recruit for you, or post your own openings and let candidates come to you."
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-stone-200">
                            <img
                                src={HERO_IMG}
                                alt="A caregiver holding the hand of an elderly patient"
                                className="w-full h-[540px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl p-5 border border-white/60">
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                                        <HeartHandshake className="w-5 h-5 text-emerald-700" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Trusted every day</div>
                                        <div className="font-outfit text-lg font-semibold text-stone-900 leading-tight mt-0.5">
                                            Families, facilities and businesses nationwide.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- Trust strip ---------- */}
            <section className="border-y border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
                    <TrustItem icon={Shield} title="Credentialed care" desc="License and background verified before day one." />
                    <TrustItem icon={HeartHandshake} title="Family-first" desc="Real people, real care — never a call center." />
                    <TrustItem icon={MapPin} title="Nationwide" desc="Coverage across all 50 states and DC." />
                    <TrustItem icon={Sparkles} title="Automated matching" desc="Smart caregiver-to-need pairing under the hood." />
                </div>
            </section>

            {/* ---------- Bottom CTA - back to pillars ---------- */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                <div className="relative rounded-3xl bg-stone-900 text-white p-10 lg:p-16 overflow-hidden grain-overlay">
                    <div className="relative z-10 max-w-2xl">
                        <HeartHandshake className="w-10 h-10 text-orange-400 mb-6" strokeWidth={1.5} />
                        <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight">
                            Ready when you are. Pick a pillar to start.
                        </h2>
                        <p className="mt-4 text-stone-300 font-dmsans max-w-xl">
                            Whether you&rsquo;re a family, a facility, or a business hiring nurses — you&rsquo;re three clicks from care.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {PILLARS.map((p) => (
                                <Link key={p.key} to={`/services/${p.key}`}>
                                    <Button className="rounded-full bg-white text-stone-900 hover:bg-orange-100 px-6 py-6 text-sm font-medium">
                                        {p.title.split("&")[0].trim()} <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            ))}
                        </div>
                    </div>
                    <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-emerald-600/20 blur-3xl" />
                </div>
            </section>

            <LaborPoolStrip />

            <footer className="border-t border-stone-200 py-8">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3 text-sm text-stone-500 font-dmsans">
                    <div>© {new Date().getFullYear()} Infini Health<span className="text-emerald-700 font-semibold">Care</span> · by CareCo 24 · <span className="text-stone-400">Powered by Infini Corp LLC</span></div>
                    <div className="flex flex-wrap gap-5">
                        <Link to="/privacy" data-testid="footer-privacy" className="hover:text-emerald-700">Privacy</Link>
                        <Link to="/terms" data-testid="footer-terms" className="hover:text-emerald-700">Terms</Link>
                        <Link to="/data-practices" data-testid="footer-data-practices" className="hover:text-emerald-700">HIPAA &amp; data</Link>
                        <Link to="/status" data-testid="footer-status" className="hover:text-emerald-700">Status</Link>
                        <a href="mailto:hello@careco24.com" className="hover:text-emerald-700">Support</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function TrustItem({ icon: Icon, title, desc }) {
    return (
        <div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.75} />
            </div>
            <div className="font-outfit font-semibold text-stone-900">{title}</div>
            <div className="text-sm text-stone-500 font-dmsans mt-1">{desc}</div>
        </div>
    );
}

function ServeItem({ icon: Icon, title, desc }) {
    return (
        <div className="flex gap-4 p-5 rounded-2xl border border-stone-200 bg-white hover:shadow-sm transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.75} />
            </div>
            <div>
                <div className="font-outfit text-lg font-semibold text-stone-900">{title}</div>
                <div className="text-sm text-stone-600 font-dmsans mt-1">{desc}</div>
            </div>
        </div>
    );
}

function LaborPoolStrip() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/labor-pools").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data || !data.totals?.caregivers) return null;
    const top = Object.entries(data.states || {})
        .sort((a, b) => b[1].total - a[1].total).slice(0, 8);
    return (
        <section className="border-t border-stone-200 bg-white py-14" data-testid="landing-labor-pool-strip">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2 flex items-center justify-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> Labor pools · nationwide
                    </div>
                    <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-stone-900 tracking-tight">
                        <span className="text-emerald-700">{data.totals.caregivers}</span> vetted caregivers
                        {" "}across <span className="text-emerald-700">{data.totals.states}</span> states
                    </h2>
                </div>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                    {top.map(([code, v]) => (
                        <Link key={code} to={`/state/${code}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 bg-stone-50 hover:border-emerald-400 hover:bg-emerald-50 transition-all">
                            <span className="font-outfit font-semibold text-sm text-stone-900">{code}</span>
                            <span className="text-xs text-stone-500 font-dmsans">{v.total} ready</span>
                        </Link>
                    ))}
                </div>
                <div className="text-center">
                    <Link to="/states" className="text-sm text-emerald-700 font-outfit font-medium inline-flex items-center gap-1">
                        See all state pools <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
